import React, { useState, useEffect } from 'react';
import { Box, Select, MenuItem, Typography, styled } from '@mui/material';
import { ReactCountryFlag } from 'react-country-flag';

const CurrencyBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',
  '& .amount-row': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2)
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  minWidth: 100,
  height: 40,
  borderRadius: '20px',
  backgroundColor: '#f5f5f5',
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(3),
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '&:hover': {
    backgroundColor: '#eeeeee'
  },
  '&.Mui-focused': {
    backgroundColor: '#eeeeee',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    }
  }
}));

const currencies = [
  { code: 'USD', name: 'US Dollar', flag: 'US' },
  { code: 'EUR', name: 'Euro', flag: 'EU' },
  { code: 'GBP', name: 'British Pound', flag: 'GB' },
  { code: 'JPY', name: 'Japanese Yen', flag: 'JP' },
  { code: 'CNY', name: 'Chinese Yuan', flag: 'CN' },
  { code: 'KRW', name: 'Korean Won', flag: 'KR' },
  { code: 'AED', name: 'UAE Dirham', flag: 'AE' },
  { code: 'RUB', name: 'Russian Ruble', flag: 'RU' },
  { code: 'THB', name: 'Thai Baht', flag: 'TH' },
  { code: 'CAD', name: 'Canadian Dollar', flag: 'CA' },
  { code: 'BRL', name: 'Brazilian Real', flag: 'BR' },
  { code: 'MXN', name: 'Mexican Peso', flag: 'MX' },
];

const CurrencySelector = ({ amount }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [rates, setRates] = useState(null);
  const [convertedAmount, setConvertedAmount] = useState(amount);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch('https://v6.exchangerate-api.com/v6/d89ca32ea3125ab8b403532b/latest/USD');
        const data = await response.json();
        if (data.result === 'success') {
          setRates(data.conversion_rates);
        } else {
          // API returned error, keep rates as null to fallback to USD
          console.warn('Exchange rate API returned error:', data);
        }
      } catch (error) {
        console.error('Error fetching rates:', error);
        // Keep rates as null to fallback to USD
      }
    };
    fetchRates();
  }, []);

  useEffect(() => {
    if (selectedCurrency === 'USD') {
      // Always show original amount for USD
      setConvertedAmount(amount);
    } else if (rates && rates[selectedCurrency]) {
      // Convert only if we have valid rates
      const converted = amount * rates[selectedCurrency];
      setConvertedAmount(converted);
    } else {
      // Fallback to USD amount if no rates available
      setConvertedAmount(amount);
    }
  }, [selectedCurrency, rates, amount]);

  return (
    <CurrencyBox>
      <Typography 
        className="amount"
        sx={{
          fontSize: '2.5rem',
          fontWeight: 700,
          color: '#000000',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }}
      >
        {convertedAmount.toLocaleString(undefined, {
          style: 'currency',
          currency: selectedCurrency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}
      </Typography>
      <StyledSelect
        value={selectedCurrency}
        onChange={(e) => setSelectedCurrency(e.target.value)}
      >
        {currencies.map((currency) => (
          <MenuItem key={currency.code} value={currency.code}>
            <ReactCountryFlag countryCode={currency.flag} svg />
            <span style={{ marginLeft: 8 }}>{currency.code}</span>
          </MenuItem>
        ))}
      </StyledSelect>
    </CurrencyBox>
  );
};

export default CurrencySelector;